import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';

const STORAGE_KEY = 'sidebarClosedSections';

const readClosed = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const writeClosed = (closed) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(closed));
  } catch {
    // Remembering open sections is a convenience - fine to skip.
  }
};

// A titled block of sidebar links that can be folded away. Open/closed is
// remembered per section in this browser, and a section opens again on its
// own when you navigate to one of its pages, so the current page's link is
// always visible. In the icon-only (collapsed) sidebar there's no room for
// titles, so the links are always shown.
const SidebarSection = ({
  id,
  title,
  routes,
  pathname,
  collapse,
  children,
}) => {
  const [open, setOpen] = useState(() => !readClosed().includes(id));

  const setAndRemember = (nextOpen) => {
    setOpen(nextOpen);
    const closed = readClosed().filter((item) => item !== id);
    writeClosed(nextOpen ? closed : [...closed, id]);
  };

  useEffect(() => {
    if (!open && routes.some((route) => pathname.startsWith(route))) {
      setAndRemember(true);
    }
    // Only when the page changes - not when the user folds the section.
    // eslint-disable-next-line
  }, [pathname]);

  const isOpen = collapse || open;

  return (
    <ul className="sidebar__block">
      {!collapse && (
        <li>
          <button
            type="button"
            className="sidebar__section-toggle"
            onClick={() => setAndRemember(!open)}
            aria-expanded={isOpen}
          >
            <span>{title}</span>
            <ChevronDownIcon
              className={`sidebar__section-chevron${
                isOpen ? ' sidebar__section-chevron--open' : ''
              }`}
            />
          </button>
        </li>
      )}
      {isOpen && children}
    </ul>
  );
};

SidebarSection.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.string),
  pathname: PropTypes.string,
  collapse: PropTypes.bool,
  children: PropTypes.node,
};

SidebarSection.defaultProps = {
  routes: [],
  pathname: '',
  collapse: false,
  children: null,
};

export default SidebarSection;
